/**
 * PROTOTYPE variant A: verdict, then facts.
 *
 * One column. The verdict is the only loud thing on the page: a single word
 * in the status colour, one sentence, one line of provenance. Facts follow as
 * label/value rows in small groups. No bordered metric grid, no per-section
 * provenance paragraph, and events sit at the bottom instead of behind a tab.
 */
import { cn } from "../../../lib/utils";
import { StatusDot, formatTimestamp } from "../DiagnosticsPanelPrimitives";
import type { StatusTone } from "../DiagnosticsSettings.types";
import { DiagnosticsTargetPicker } from "../DiagnosticsTargetPicker";
import type { PrototypeFactGroup } from "./diagnosticsPrototypeModel";
import { PrototypeRefreshButton } from "./PrototypeRefreshButton";
import type { DiagnosticsPrototypeVariantProps } from "./diagnosticsPrototypeProps";

const VERDICT_TEXT: Readonly<Record<StatusTone, string>> = {
  ready: "text-success-foreground",
  warning: "text-warning-foreground",
  error: "text-destructive-foreground",
  info: "text-info-foreground",
  muted: "text-muted-foreground",
};

function FactGroup({ group }: { readonly group: PrototypeFactGroup }) {
  return (
    <section className="min-w-0">
      <h3 className="text-sm font-medium text-foreground">{group.title}</h3>
      <p className="mt-0.5 text-xs text-muted-foreground">{group.source}</p>
      <dl className="mt-3 space-y-2">
        {group.facts.map((fact) => (
          <div key={fact.label} className="grid grid-cols-[7.5rem_minmax(0,1fr)] gap-3 text-sm">
            <dt className="text-muted-foreground">{fact.label}</dt>
            <dd className="flex min-w-0 items-center gap-2 text-foreground">
              {fact.tone ? <StatusDot tone={fact.tone} /> : null}
              <span className={cn("truncate", fact.mono ? "font-mono text-xs" : null)}>
                {fact.value}
              </span>
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

export function DiagnosticsPrototypeVariantA({
  model,
  targets,
  refresh,
}: DiagnosticsPrototypeVariantProps) {
  return (
    <div className="space-y-10 px-3 sm:px-4">
      <div className="flex flex-wrap items-center gap-2">
        <DiagnosticsTargetPicker
          groups={targets.groups}
          active={targets.active}
          onSelect={targets.onSelect}
        />
        <PrototypeRefreshButton refresh={refresh} />
      </div>

      <div className="max-w-2xl">
        <p
          className={cn(
            "text-3xl font-semibold tracking-[-0.03em]",
            VERDICT_TEXT[model.verdict.tone],
          )}
        >
          {model.verdict.label}
        </p>
        <p className="mt-2 text-base text-foreground">{model.verdict.sentence}</p>
        <p className="mt-1 text-sm text-muted-foreground">
          Observed {formatTimestamp(model.verdict.observedAt)}. {model.verdict.source}
        </p>
        {model.verdict.disagreement ? (
          <p className="mt-3 rounded-md border border-warning-border bg-warning-surface px-3 py-2 text-sm text-warning-foreground">
            {model.verdict.disagreement}
          </p>
        ) : null}
      </div>

      <div className="grid gap-x-10 gap-y-8 sm:grid-cols-2">
        {model.groups.map((group) => (
          <FactGroup key={group.id} group={group} />
        ))}
      </div>

      <section>
        <h3 className="text-sm font-medium text-foreground">Recent events</h3>
        {model.events.length === 0 ? (
          <p className="mt-2 text-sm text-muted-foreground">
            No connection failures or blocked checks on record.
          </p>
        ) : (
          <ul className="mt-3 space-y-2">
            {model.events.map((event) => (
              <li key={`${event.title}-${event.at}`} className="flex gap-3 text-sm">
                <span className="w-24 shrink-0 text-muted-foreground">
                  {formatTimestamp(event.at)}
                </span>
                <span className="mt-1.5">
                  <StatusDot tone={event.tone} />
                </span>
                <span className="min-w-0">
                  <span className="text-foreground">{event.title}</span>
                  <span className="block text-muted-foreground">{event.detail}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
